import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SkeletonText,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { ISubscription, noop, routes } from '@blockpulse3/data/shared';
import { useGetSubscriptionQuery } from '@blockpulse3/graphql/hooks';
import {
  ErrorQueryCard,
  ResponsiveModal,
  ResponsiveModalFooter,
  ResponsiveModalProps,
} from '@blockpulse3/ui/commons';

type Props = {
  onCancel?: () => void;
  onModify?: () => void;
  subscriptionId: ISubscription['id'];
  subCompanyId?: string;
} & Omit<ResponsiveModalProps, 'children'>;

export function CompanyVerificationModal({
  onCancel = noop,
  onModify = noop,
  subscriptionId,
  subCompanyId,
  ...props
}: Props): JSX.Element {
  const i18nInvitationsVerifyUserModal = useTranslations('Invitations.VerificationModal');
  const i18nInvitationsVerifyUserCard = useTranslations(
    'Invitations.VerificationModal.CompanyCard',
  );

  const navigate = useNavigate();

  const { companyId = '' } = useParams();

  const { data, loading, error } = useGetSubscriptionQuery({
    variables: { subscriptionId },
    skip: !subscriptionId,
  });

  const { subscription } = data || {};
  const { id = '' } = subscription || {};

  const handleVerificationValidate = (): void => {
    if (companyId) {
      navigate(routes.workspace.subscription.fmt(companyId, id));
    }
    if (subCompanyId) {
      navigate(routes.workspace.subscription.fmt(subCompanyId, id));
    }
  };

  return (
    <ResponsiveModal {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{i18nInvitationsVerifyUserModal('VerifyInformations')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody as={Stack} spacing="4">
          <Stack fontSize="sm" spacing="0">
            <Text fontWeight="500">{i18nInvitationsVerifyUserModal('ModalDescription-1')}</Text>
            <Text fontWeight="500">
              {i18nInvitationsVerifyUserModal.rich('ModalDescription-2', {
                important: (chunks) => (
                  <Text as="span" fontWeight="700" textDecoration="underline">
                    {chunks}
                  </Text>
                ),
              })}
            </Text>
          </Stack>
          {!error ? (
            <Card bg="gray.50" boxShadow="none">
              <CardHeader>
                <Text color="gray.700" fontWeight="600">
                  {i18nInvitationsVerifyUserCard('ModalCardTitle')}
                </Text>
              </CardHeader>
              <CardBody as={Stack} spacing="4">
                <Divider />
                <Stack fontSize="sm" spacing="0">
                  <Text fontWeight="500">{i18nInvitationsVerifyUserCard('MoralPerson')}</Text>
                  <SkeletonText isLoaded={!loading} noOfLines={1} skeletonHeight="4">
                    <Text fontWeight="600">{subscription?.identity?.companyIdentity?.name}</Text>
                  </SkeletonText>
                </Stack>
                <Divider />
                <Stack fontSize="sm" spacing="0">
                  <Text fontWeight="500">
                    {i18nInvitationsVerifyUserCard('IdentificationEntityField')}
                  </Text>
                  <SkeletonText isLoaded={!loading} noOfLines={1} skeletonHeight="4">
                    <Text fontWeight="600">
                      {subscription?.identity?.companyIdentity?.registrationEntity}
                    </Text>
                  </SkeletonText>
                </Stack>
                <Divider />
                <Stack fontSize="sm" spacing="0">
                  <Text fontWeight="500">{i18nInvitationsVerifyUserCard('Address')}</Text>
                  <SkeletonText isLoaded={!loading} noOfLines={1} skeletonHeight="4">
                    <Text fontWeight="600">
                      {subscription?.identity?.companyIdentity?.address?.formatted}
                    </Text>
                  </SkeletonText>
                </Stack>
                <Divider />
                <Stack fontSize="sm" spacing="0">
                  <Text fontWeight="500">{i18nInvitationsVerifyUserCard('RepresentedBy')}</Text>
                  <SkeletonText isLoaded={!loading} noOfLines={1} skeletonHeight="4">
                    <Text fontWeight="600">
                      {subscription?.identity?.companyIdentity?.signer?.individualIdentity?.name}
                    </Text>
                  </SkeletonText>
                </Stack>
              </CardBody>
            </Card>
          ) : (
            <ErrorQueryCard h="360px" mt="4" />
          )}
        </ModalBody>
        <Divider />
        <ResponsiveModalFooter>
          <Button variant="secondary" onClick={onCancel}>
            {i18nInvitationsVerifyUserModal('Cancel')}
          </Button>
          <Button variant="secondary" onClick={onModify}>
            {i18nInvitationsVerifyUserModal('Modify')}
          </Button>
          <Button isDisabled={!!error || loading} onClick={handleVerificationValidate}>
            {i18nInvitationsVerifyUserModal('GoNext')}
          </Button>
        </ResponsiveModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
}

export type CompanyVerificationModalProps = Props;
